/**
 * @file apps/web/src/components/DiscoveryPanel.tsx
 * @description Slide-over detail panel for a single Discovery skill-tree
 * node. Shows the NetSuite rule's reference content (FDD suggestion /
 * limitations, read-only), an optional AI suggestion from a matched
 * workshop-transcript requirement — advisory only, one-click accept, tagged
 * with the confidence claudeStub.matchRequirementsToRules() assigned it
 * (see uploadPipeline.ts's extractRequirementsAndSuggestDiscovery) — and an
 * editable disposition selector and notes field for the human's actual
 * decision. Build tracking (formerly a "Mark as built" toggle here) now
 * lives on the dedicated Build page against design items — see
 * BuildPage.tsx.
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { X, Save, Loader2, AlertCircle, Sparkles, FileText, ExternalLink, RefreshCw } from 'lucide-react';
import { Badge, type BadgeVariant } from './Badge.js';
import { discoveryApi } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';
import { formatTimestamp } from '../lib/formatTimestamp.js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface DiscoveryRuleReference {
  nativeSupport:          string;
  requiresCustomization:  string | null;
  limitations:            string[];
  fddSuggestion:          string;
}

export interface DiscoveryNode {
  id:                      string;
  rule_id:                 string;
  module:                  string;
  process_area:            string;
  process_name:            string;
  disposition:             string;
  notes:                   string | null;
  resolved_at:             string | null;
  rule_reference:          DiscoveryRuleReference | null;
  // Advisory suggestion from a matched workshop-transcript requirement —
  // null when nothing has ever matched this node, or once a human has set
  // `disposition` (accept or override both clear it — see PATCH
  // /projects/:id/discovery/:nodeId).
  suggested_disposition:   string | null;
  suggestion_reason:       string | null;
  // Confidence claudeStub.matchRequirementsToRules() assigned this match —
  // 'low' is dropped server-side before a suggestion is ever written, so
  // this is always 'high' | 'medium' | null in practice.
  suggestion_confidence:   string | null;
  // The client's own words behind the suggestion, verified against the
  // transcript server-side before being stored (null when none could be
  // verified), and where in the recording they start. suggestion_quote_at_secs
  // is a Postgres NUMERIC, so it arrives as a string.
  suggestion_quote:          string | null;
  // Who said it — read from the transcript's own speaker label, null when the
  // transcript has none.
  suggestion_quote_speaker:  string | null;
  suggestion_quote_at_secs:  string | number | null;
}

interface DiscoveryPanelProps {
  node:        DiscoveryNode | null;
  projectId:   string;
  onClose:     () => void;
  onSave:      (nodeId: string, updates: { disposition?: string; notes?: string }) => Promise<void>;
}

// docs/NEXT-PHASE-SCOPING.md §2 — this node's current FDD, from
// GET /projects/:id/discovery/:nodeId/fdd. 'generated' vs 'edited' is the
// existing document_sections.ai_generated signal (see that route's
// comment), not a new concept — 'edited' means at least one section has
// been hand-touched since generation.
interface NodeFddStatus {
  status:     'none' | 'generated' | 'edited';
  documentId: string | null;
  title:      string | null;
}

const DISPOSITION_OPTIONS: string[] = [
  'not_reviewed',
  'out_of_box',
  'in_erp_custom',
  'ootb_custom',
  'not_applicable',
];

// Subtitle shown as a native title-attribute tooltip on hover — the two
// customization tiers read as easily confused ("ERP Supported
// Customization" vs. "Customization") without a one-line reminder of what
// each actually means. No tooltip library — this file already uses plain
// `title=` for the confidence badge above; same pattern.
const DISPOSITION_SUBTITLES: Partial<Record<string, string>> = {
  ootb_custom:   'Workflows, custom fields, saved searches. No code.',
  in_erp_custom: 'SuiteScript or custom development.',
};

// ---------------------------------------------------------------------------
// DiscoveryPanel
// ---------------------------------------------------------------------------

export function DiscoveryPanel({ node, projectId, onClose, onSave }: DiscoveryPanelProps) {
  const { devHeaders } = useAuth();

  const [disposition, setDisposition] = useState(node?.disposition ?? 'not_reviewed');
  const [notes, setNotes]             = useState(node?.notes ?? '');
  const [saving, setSaving]           = useState(false);
  const [error, setError]             = useState<string | null>(null);

  // Per-node FDD (docs/NEXT-PHASE-SCOPING.md §2) — fetched only for nodes
  // that can actually have one, gated on the node's real (saved)
  // disposition, not the in-progress picker selection below (that only
  // becomes real once Save is clicked, which closes this panel — see
  // handleSave). fddStatus starts null (nothing rendered) until the fetch
  // resolves, distinct from the loaded {status:'none',...} response.
  const [fddStatus, setFddStatus]     = useState<NodeFddStatus | null>(null);
  const [fddLoading, setFddLoading]   = useState(false);
  const [generating, setGenerating]   = useState(false);
  const [fddError, setFddError]       = useState<string | null>(null);

  useEffect(() => {
    setDisposition(node?.disposition ?? 'not_reviewed');
    setNotes(node?.notes ?? '');
    setError(null);
    setFddStatus(null);
    setFddError(null);
  }, [node?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!node || node.disposition !== 'in_erp_custom') return;
    let cancelled = false;
    setFddLoading(true);
    discoveryApi.getNodeFdd(projectId, node.id, devHeaders)
      .then((data) => { if (!cancelled) setFddStatus(data); })
      .catch(() => { if (!cancelled) setFddStatus(null); })
      .finally(() => { if (!cancelled) setFddLoading(false); });
    return () => { cancelled = true; };
  }, [node?.id, node?.disposition]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!node) return null;

  // Generation is a real Claude call (packages/stubs/src/claude.ts) and
  // takes real time — async with its own loading state, same pattern
  // DocumentsPage.tsx's handleGenerate uses for the project-wide FDD, not
  // a blocking wait on the whole panel.
  const handleGenerateFdd = async () => {
    if (fddStatus && fddStatus.status !== 'none') {
      if (!confirm('Regenerate this FDD? This replaces the current draft content — the previous version stays in its version history.')) return;
    }
    setGenerating(true);
    setFddError(null);
    try {
      await discoveryApi.generateNodeFdd(projectId, node.id, devHeaders);
      const refreshed = await discoveryApi.getNodeFdd(projectId, node.id, devHeaders);
      setFddStatus(refreshed);
    } catch (e) {
      setFddError(e instanceof Error ? e.message : 'Failed to generate FDD');
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(node.id, { disposition, notes });
      setError(null);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save disposition');
    } finally {
      setSaving(false);
    }
  };

  // One-click accept — sets disposition to exactly what was suggested and
  // saves immediately through the same PATCH as a manual save (which also
  // clears the suggestion columns server-side). Never happens automatically
  // — this only runs when the person viewing the node clicks the button.
  const handleAcceptSuggestion = async () => {
    if (!node.suggested_disposition) return;
    setDisposition(node.suggested_disposition);
    setSaving(true);
    try {
      await onSave(node.id, { disposition: node.suggested_disposition, notes });
      setError(null);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to accept suggestion');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {/* Backdrop — click to dismiss, matches CSVImportModal's overlay convention */}
      <div
        className="fixed inset-0 bg-ink/20 z-40"
        onClick={onClose}
      />
      {/* Slide-over panel */}
      <div className="fixed top-0 right-0 h-full w-full max-w-md bg-white z-50 shadow-2xl flex flex-col">
        <div className="flex items-start justify-between gap-3 p-5 border-b border-[rgba(8,17,31,0.08)]">
          <div className="min-w-0">
            <p className="text-[11px] font-mono text-muted">{node.module} · {node.process_area}</p>
            <h3 className="font-display text-base font-semibold text-ink mt-0.5">{node.process_name}</h3>
            <p className="text-[10px] font-mono text-muted/70 mt-1">{node.rule_id}</p>
          </div>
          <button onClick={onClose} className="btn-icon shrink-0" aria-label="Close">
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {node.suggested_disposition && (
            <div className="bg-warning/8 border border-warning/25 rounded-lg p-3 space-y-2.5">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-1.5 text-[11px] font-mono font-semibold text-warning uppercase tracking-wider">
                  <Sparkles size={12} />
                  Suggested from Workshop
                </div>
                {node.suggestion_confidence && (
                  <span
                    className={[
                      'text-[10px] font-mono px-1.5 py-0.5 rounded-full uppercase tracking-wider shrink-0',
                      node.suggestion_confidence === 'medium'
                        ? 'bg-warning/15 text-warning'
                        : 'bg-success/12 text-success',
                    ].join(' ')}
                    title={node.suggestion_confidence === 'medium'
                      ? 'A real but uncertain match — worth a second look before accepting.'
                      : 'A clear, unambiguous match.'}
                  >
                    {node.suggestion_confidence} confidence
                  </span>
                )}
              </div>
              {node.suggestion_quote && (
                <figure className="border-l-2 border-warning/50 pl-3">
                  <blockquote className="text-sm text-ink italic leading-relaxed">
                    “{node.suggestion_quote}”
                  </blockquote>
                  {(node.suggestion_quote_speaker || formatTimestamp(node.suggestion_quote_at_secs)) && (
                    <figcaption className="mt-1 text-[11px] font-mono text-muted">
                      {[
                        node.suggestion_quote_speaker,
                        formatTimestamp(node.suggestion_quote_at_secs)
                          ? `at ${formatTimestamp(node.suggestion_quote_at_secs)} in the recording`
                          : null,
                      ].filter(Boolean).join(' · ')}
                    </figcaption>
                  )}
                </figure>
              )}
              {node.suggestion_reason && (
                <p className="text-sm text-ink/80 leading-relaxed">{node.suggestion_reason}</p>
              )}
              <div className="flex items-center gap-2">
                <Badge variant={node.suggested_disposition as BadgeVariant} size="xs" />
                <button
                  onClick={() => void handleAcceptSuggestion()}
                  disabled={saving}
                  className="btn-secondary text-xs py-1 px-2"
                >
                  {saving ? <Loader2 size={12} className="animate-spin" /> : null}
                  Accept
                </button>
              </div>
              <p className="text-[11px] text-muted">
                Advisory only — nothing is set until you accept it, or set a different disposition below yourself.
              </p>
            </div>
          )}

          {node.rule_reference && (
            <div className="bg-sky/40 rounded-lg p-3 space-y-2">
              <p className="text-[11px] font-mono font-semibold text-blue uppercase tracking-wider">
                NetSuite Reference
              </p>
              <p className="text-sm text-ink/80 leading-relaxed">{node.rule_reference.fddSuggestion}</p>
              {node.rule_reference.limitations.length > 0 && (
                <ul className="text-xs text-ink/70 space-y-1 list-disc pl-4">
                  {node.rule_reference.limitations.map((l, i) => <li key={i}>{l}</li>)}
                </ul>
              )}
            </div>
          )}

          <div>
            <label className="label mb-1.5">Disposition</label>
            <div className="flex flex-wrap gap-1.5">
              {DISPOSITION_OPTIONS.map((value) => (
                <button
                  key={value}
                  onClick={() => setDisposition(value)}
                  title={DISPOSITION_SUBTITLES[value]}
                  className={[
                    'text-xs font-mono px-2.5 py-1.5 rounded-full border transition-colors',
                    disposition === value
                      ? 'border-blue bg-sky text-blue'
                      : 'border-[rgba(8,17,31,0.12)] text-muted hover:text-ink',
                  ].join(' ')}
                >
                  <Badge variant={value as BadgeVariant} size="xs" className={disposition === value ? '' : 'opacity-60'} />
                </button>
              ))}
            </div>
          </div>

          {/* Per-node FDD (docs/NEXT-PHASE-SCOPING.md §2) — only for nodes
              actually disposed (saved, not just picked above) as
              in_erp_custom. Never shown for ootb_custom — that tier has no
              FDD of its own by design. */}
          {node.disposition === 'in_erp_custom' && (
            <div>
              <label className="label mb-1.5">Functional Design Document</label>
              {fddLoading ? (
                <div className="h-9 rounded-lg bg-gray/60 animate-pulse" />
              ) : (
                <div className="flex items-center justify-between gap-2 p-2.5 rounded-lg border border-[rgba(8,17,31,0.08)]">
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText size={14} className="text-muted shrink-0" />
                    {!fddStatus || fddStatus.status === 'none' ? (
                      <span className="text-xs text-muted">Not generated yet</span>
                    ) : (
                      <>
                        <span
                          className={[
                            'text-[10px] font-mono px-1.5 py-0.5 rounded-full uppercase tracking-wider shrink-0',
                            fddStatus.status === 'edited' ? 'bg-sky text-blue' : 'bg-success/12 text-success',
                          ].join(' ')}
                          title={fddStatus.status === 'edited'
                            ? 'At least one section has been hand-edited since it was generated.'
                            : 'Every section is exactly what AI generated — nothing hand-edited yet.'}
                        >
                          {fddStatus.status}
                        </span>
                        {fddStatus.documentId && (
                          <Link
                            to={`/projects/${projectId}/documents?doc=${fddStatus.documentId}`}
                            className="text-xs text-blue hover:underline truncate flex items-center gap-1"
                          >
                            {fddStatus.title} <ExternalLink size={11} className="shrink-0" />
                          </Link>
                        )}
                      </>
                    )}
                  </div>
                  <button
                    onClick={() => void handleGenerateFdd()}
                    disabled={generating}
                    className="btn-secondary text-xs py-1 px-2 shrink-0"
                  >
                    {generating
                      ? <Loader2 size={12} className="animate-spin" />
                      : fddStatus && fddStatus.status !== 'none' ? <RefreshCw size={12} /> : <Sparkles size={12} />
                    }
                    {generating ? 'Generating…' : fddStatus && fddStatus.status !== 'none' ? 'Regenerate' : 'Generate FDD'}
                  </button>
                </div>
              )}
              {fddError && (
                <div className="flex items-center gap-2 mt-1.5 text-xs text-danger">
                  <AlertCircle size={12} className="shrink-0" /> {fddError}
                </div>
              )}
            </div>
          )}

          <div>
            <label className="label mb-1.5">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Discovery notes, client context, decisions made…"
              className="input text-sm min-h-[120px] resize-none"
              rows={5}
            />
          </div>
        </div>

        <div className="p-5 border-t border-[rgba(8,17,31,0.08)] space-y-3">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-danger/8 rounded-lg text-danger text-sm">
              <AlertCircle size={14} className="shrink-0" /> {error}
            </div>
          )}
          <div className="flex justify-end gap-2">
            <button onClick={onClose} className="btn-ghost text-sm" disabled={saving}>Cancel</button>
            <button onClick={() => void handleSave()} disabled={saving} className="btn-primary text-sm">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
